import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import type { Channel, ConversationStatus, Direction } from '@tips-crm/shared';
import {
  fetchConversations,
  type ConversationFilters,
  type ConversationListItem,
} from '../api/endpoints.js';
import { PageHeader } from '../components/PageHeader.js';
import { DataTable, type Column } from '../components/DataTable.js';

const CHANNELS: Channel[] = ['voice', 'chat'];
const DIRECTIONS: Direction[] = ['inbound', 'outbound'];
const STATUSES: ConversationStatus[] = [
  'initiating',
  'queued',
  'active',
  'completed',
  'failed',
  'abandoned',
];

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

export function ConversationsPage() {
  const navigate = useNavigate();
  const [filters, setFilters] = useState<ConversationFilters>({});

  const query = useQuery({
    queryKey: ['conversations', filters],
    queryFn: () => fetchConversations({ ...filters, limit: 50 }),
  });

  const columns = useMemo<Column<ConversationListItem>[]>(
    () => [
      {
        key: 'contact',
        header: 'Contact',
        render: (c) =>
          c.contact_name || c.contact_email ? (
            <div className="cell-stack">
              <span>{c.contact_name ?? c.contact_email}</span>
              {c.contact_name && c.contact_email && (
                <span className="small muted">{c.contact_email}</span>
              )}
            </div>
          ) : (
            <span className="muted">{c.source_phone_number_e164 ?? '—'}</span>
          ),
      },
      {
        key: 'channel',
        header: 'Channel',
        render: (c) => <span className={`badge badge--${c.channel}`}>{c.channel}</span>,
      },
      { key: 'direction', header: 'Direction', render: (c) => c.direction },
      {
        key: 'status',
        header: 'Status',
        render: (c) => <span className={`badge badge--status-${c.status}`}>{c.status}</span>,
      },
      { key: 'disposition', header: 'Disposition', render: (c) => c.disposition ?? '—' },
      { key: 'started', header: 'Started', render: (c) => formatDate(c.started_at) },
    ],
    [],
  );

  function update<K extends keyof ConversationFilters>(key: K, value: ConversationFilters[K]) {
    setFilters((prev) => ({ ...prev, [key]: value || undefined }));
  }

  return (
    <div className="page">
      <PageHeader title="Conversations" subtitle="All voice and chat interactions." />

      <div className="toolbar toolbar--wrap">
        <input
          className="field__input search-input"
          type="search"
          placeholder="Search name, email, or phone…"
          value={filters.q ?? ''}
          onChange={(e) => update('q', e.target.value)}
        />
        <select
          className="field__input"
          value={filters.channel ?? ''}
          onChange={(e) => update('channel', (e.target.value || undefined) as Channel | undefined)}
        >
          <option value="">All channels</option>
          {CHANNELS.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
        <select
          className="field__input"
          value={filters.direction ?? ''}
          onChange={(e) =>
            update('direction', (e.target.value || undefined) as Direction | undefined)
          }
        >
          <option value="">All directions</option>
          {DIRECTIONS.map((d) => (
            <option key={d} value={d}>{d}</option>
          ))}
        </select>
        <select
          className="field__input"
          value={filters.status ?? ''}
          onChange={(e) =>
            update('status', (e.target.value || undefined) as ConversationStatus | undefined)
          }
        >
          <option value="">All statuses</option>
          {STATUSES.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
        <label className="field field--inline">
          <span className="field__label">From</span>
          <input
            className="field__input"
            type="date"
            value={filters.from ?? ''}
            onChange={(e) => update('from', e.target.value)}
          />
        </label>
        <label className="field field--inline">
          <span className="field__label">To</span>
          <input
            className="field__input"
            type="date"
            value={filters.to ?? ''}
            onChange={(e) => update('to', e.target.value)}
          />
        </label>
      </div>

      <DataTable
        columns={columns}
        rows={query.data?.items}
        rowKey={(c) => c.id}
        isLoading={query.isLoading}
        isError={query.isError}
        error={query.error}
        onRetry={() => query.refetch()}
        onRowClick={(c) => navigate(`/conversations/${c.id}`)}
        emptyMessage="No conversations match these filters."
      />
    </div>
  );
}
